/**
 * 問題一覧取得カスタムフック
 * GET /questions API との連携
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../api/client";
import { Question } from "../types";
import { getPack, queryPack } from "../offline/packStore";

interface QuestionsData {
  questions: Question[];
  total: number;
  limit: number;
  offset: number;
}

export interface QuestionFilter {
  examType?: string;
  examCategory?: string; // Phase 2〜: 'AP' | 'COMMON' | 'SC' | 'NW' | ...
  examSection?: string; // Phase 2〜: 'morning' | 'morning1' | 'morning2' | ...
  year?: number;
  season?: string;
  category?: string;
  shuffle?: boolean;
  search?: string;
  bookmarkedOnly?: boolean;
  incorrectOnly?: boolean; // 最新回答が誤答の問題のみ（復習用）
}

interface UseQuestionsOptions extends QuestionFilter {
  limit?: number;
  offset?: number;
  idToken?: string;
  bookmarkIds?: Set<string>;
  hasImage?: boolean;
}

interface UseQuestionsResult {
  questions: Question[];
  total: number;
  isLoading: boolean;
  error: string | null;
  isOffline: boolean;
  refetch: () => void;
}

/**
 * fetch の失敗がネットワーク起因かどうかを判定する。
 * サーバーが到達できてエラーステータス（4xx/5xx）を返した場合はオフラインパックへ
 * フォールバックしない（サーバー側の実データを優先する）。
 * design: .steering/20260719-capacitor-store-app/design.md §4.4
 */
function isNetworkCausedError(err: unknown): boolean {
  return err instanceof TypeError || navigator.onLine === false;
}

/**
 * 問題一覧を API から取得するフック
 */
export function useQuestions(
  options: UseQuestionsOptions = {},
): UseQuestionsResult {
  const {
    examType,
    examCategory,
    examSection,
    year,
    season,
    category,
    shuffle,
    search,
    bookmarkedOnly,
    incorrectOnly,
    limit = 20,
    offset = 0,
    idToken,
    bookmarkIds,
    hasImage,
  } = options;

  // ブックマークIDが出題条件になるのは bookmarkedOnly のときだけ。Set はブックマークを
  // 付け外しするたびに新しい参照になるため、そのまま依存に置くと演習中のブックマーク操作で
  // 再取得が走り、shuffle 指定のモードでは出題そのものが差し替わってしまう。
  // 安定した文字列キーに畳んでから依存に使う。
  const bookmarkIdsKey =
    bookmarkedOnly && bookmarkIds ? Array.from(bookmarkIds).sort().join(",") : "";

  const [questions, setQuestions] = useState<Question[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);

  const fetchQuestions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsOffline(false);

    try {
      const params: Record<string, string | number> = { limit, offset };
      // exam_category を優先、なければ exam_type（後方互換）
      if (examCategory) params.exam_category = examCategory;
      else if (examType) params.exam_type = examType;
      if (examSection) params.exam_section = examSection;
      if (year) params.year = year;
      if (season) params.season = season;
      if (category) params.category = category;
      if (shuffle) params.shuffle = "true";
      if (search) params.search = search;
      if (bookmarkedOnly) {
        // ブックマーク0件なら API を呼ばず空結果にする（全問題が返る誤動作を防ぐ）
        if (!bookmarkIdsKey) {
          setQuestions([]);
          setTotal(0);
          return;
        }
        params.ids = bookmarkIdsKey;
      }
      if (hasImage) params.has_image = "true";
      if (incorrectOnly) params.incorrect_only = "true";

      const data = await apiGet<QuestionsData>("/questions", params, idToken);
      setQuestions(data.questions);
      setTotal(data.total);
    } catch (err) {
      // オフラインパックへのフォールバック（design §4.4）。
      // 検索・ブックマークのみ・誤答のみはパックに無いデータに依存するため対象外
      const unsupportedOffline = Boolean(search || bookmarkedOnly || incorrectOnly);
      if (examCategory && !unsupportedOffline && isNetworkCausedError(err)) {
        const pack = await getPack(examCategory);
        if (pack) {
          const result = queryPack(pack, {
            examSection,
            year,
            season,
            category,
            shuffle,
            limit,
            offset,
          });
          setQuestions(result.questions);
          setTotal(result.total);
          setIsOffline(true);
          return;
        }
      }

      // オフライン起因の失敗は、生の fetch エラー（"Load failed (ホスト名)" など）ではなく
      // 利用者が次の行動を取れる案内にする。ここへ来るのは上のパック探索が空振りしたとき
      // ＝この区分がまだ端末に保存されていないケース。
      const message = isNetworkCausedError(err)
        ? unsupportedOffline
          ? "検索・ブックマークのみ・誤答のみの表示はオンラインでのみ利用できます"
          : "オフラインです。この区分の問題は端末に保存されていないため出題できません。通信できる場所で「過去問演習」画面から保存するか、保存済みの区分をお試しください"
        : err instanceof Error
          ? err.message
          : "問題の取得に失敗しました";
      setError(message);
      console.error("useQuestions エラー:", err);
    } finally {
      setIsLoading(false);
    }
  }, [
    examType,
    examCategory,
    examSection,
    year,
    season,
    category,
    shuffle,
    search,
    bookmarkedOnly,
    incorrectOnly,
    bookmarkIdsKey,
    hasImage,
    limit,
    offset,
    idToken,
  ]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return {
    questions,
    total,
    isLoading,
    error,
    isOffline,
    refetch: fetchQuestions,
  };
}
